import { useState, useEffect, useRef } from "react";

import { FaClock, FaMapMarkerAlt, FaArrowLeft } from "react-icons/fa";

import { POPULAR_CITIES } from "../constants";

/* ---------------- Mobile full-page picker (Cities + Points) ---------------- */
const MobileLocationPicker = ({
  open,
  mode, // 'from' | 'to'
  items, // flat list of { value, label, kind, city, pointType }
  recent,
  onPick,
  onClose,
}) => {
  const [q, setQ] = useState("");
  const contentRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 50);
    } else {
      setQ("");
    }
  }, [open]);

  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [q]);

  if (!open) return null;

  const needle = q.trim().toLowerCase();

  const filtered = needle
    ? items.filter((it) => {
        const a = (it.label || "").toLowerCase();
        const b = (it.city || "").toLowerCase();
        return a.includes(needle) || b.includes(needle);
      })
    : items;

  const cities = filtered.filter((x) => x.kind === "city");
  const points = filtered.filter((x) => x.kind === "point");

  const recents = recent?.[mode] || [];

  return (
    <div className="lg:hidden fixed inset-0 z-[10000] bg-white flex flex-col min-h-[100dvh]">
      {/* Safe area for notch */}
      <div style={{ height: "max(env(safe-area-inset-top), 8px)" }} />

      {/* Header */}
      <div className="px-4 border-b">
        <div className="flex items-center gap-3 min-h-[72px]">
          <button
            type="button"
            onClick={onClose}
            className="appearance-none w-11 h-11 rounded-full border border-gray-200 bg-white flex items-center justify-center shrink-0"
            aria-label="Back"
          >
            <FaArrowLeft className="text-[15px] text-gray-800" />
          </button>

          <div className="text-[18px] leading-none font-semibold text-gray-900">
            {mode === "from"
              ? "Select Boarding Location"
              : "Select Dropping Location"}
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="px-4 py-3 border-b">
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search city or point"
          className="w-full rounded-xl border px-4 py-3 text-base outline-none"
          type="search"
          inputMode="search"
          enterKeyHint="search"
          style={{ fontSize: 16, WebkitTextSizeAdjust: "100%" }}
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
        />
      </div>

      <div ref={contentRef} className="flex-1 overflow-y-auto">
        {/* Recent */}
        <div className="px-4 pt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-2">
            <FaClock className="opacity-70" />
            Recent
          </div>

          {recents.length === 0 ? (
            <div className="text-sm text-gray-400">No recent searches</div>
          ) : (
            <div className="mb-3 divide-y rounded-xl border border-gray-100 overflow-hidden">
              {recents.map((val, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="w-full flex items-center gap-3 px-3 py-3 text-left active:bg-gray-50"
                  onClick={() => onPick(val)}
                >
                  <FaMapMarkerAlt className="text-gray-500" />
                  <span className="text-base font-medium text-gray-800">
                    {val}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Cities */}
        <div className="px-4 pt-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            {needle ? "Matching Cities" : "Cities"}
          </div>

          {cities.length === 0 ? (
            <div className="text-sm text-gray-400 pb-3">No cities</div>
          ) : (
            <div className="divide-y rounded-xl border border-gray-100 overflow-hidden">
              {cities.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  className="w-full text-left px-3 py-3 active:bg-gray-50"
                  onClick={() => onPick(c.value)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-base font-semibold">{c.label}</span>
                    <span className="text-[10px] px-2 py-1 rounded-full bg-gray-50 text-gray-500">
                      CITY
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Points */}
        <div className="px-4 pt-4 pb-6">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            {mode === "from" ? "Boarding Points" : "Dropping Points"}
          </div>

          {points.length === 0 ? (
            <div className="text-sm text-gray-400">No points</div>
          ) : (
            <div className="divide-y rounded-xl border border-gray-100 overflow-hidden">
              {points.map((p) => (
                <button
                  key={`${p.city}-${p.value}-${p.pointType}`}
                  type="button"
                  className="w-full text-left px-3 py-3 active:bg-gray-50"
                  onClick={() => onPick(p.value)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-base font-semibold truncate">
                        {p.label}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {p.city}
                      </div>
                    </div>
                    <span className="text-[10px] px-2 py-1 rounded-full border bg-white text-gray-600 shrink-0">
                      {mode === "from" ? "BOARD AT" : "DROP AT"}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Popular Cities quick pick */}
          <div className="pt-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Popular Cities
            </div>
            <div className="flex flex-wrap gap-2">
              {POPULAR_CITIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="px-3 py-1.5 rounded-full border text-sm active:bg-red-50"
                  onClick={() => onPick(c)}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Safe area bottom */}
      <div style={{ height: "env(safe-area-inset-bottom)" }} />
    </div>
  );
};

export default MobileLocationPicker;
